/**
 * Book reader (§16).
 *
 * A book is the one format where the reading experience *is* the feature, so
 * this is the least chrome of any viewer: a chapter list, and a measured
 * column of text. No page edges — an EPUB has no pages until a reading system
 * lays it out, and drawing boundaries would invent them (the same reason the
 * Word view does not).
 *
 * The text arrives already sanitised. Scripts, handlers and remote references
 * were removed at parse time rather than escaped here, so there is no path by
 * which this component could render one (§30).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { EpubBlock, EpubChapter, EpubModel } from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface EpubViewerProps {
  readonly model: EpubModel;
  readonly current: number;
  readonly search: string;
  readonly zoom: number;
  readonly onSelect: (index: number) => void;
}

export function EpubViewer({
  model, current, search, zoom, onSelect,
}: EpubViewerProps): React.JSX.Element {
  const column = useRef<HTMLDivElement>(null);

  const imageUrls = useMemo(() => {
    const urls = new Map<string, string>();
    for (const [part, image] of model.images) {
      urls.set(part, URL.createObjectURL(new Blob([image.bytes as BlobPart], { type: image.contentType })));
    }
    return urls;
  }, [model.images]);

  useEffect(() => () => {
    for (const url of imageUrls.values()) URL.revokeObjectURL(url);
  }, [imageUrls]);

  // Moving to a chapter means starting at its beginning, which a scroll
  // container will not do on its own.
  useEffect(() => { column.current?.scrollTo({ top: 0 }); }, [current]);

  const chapter = model.chapters[current] ?? model.chapters[0];
  if (!chapter) return <div className="canvas" />;

  return (
    <div className="book">
      <nav className="book-toc" aria-label="목차">
        <div className="book-toc__head">
          <span className="book-toc__title truncate">{model.metadata.title}</span>
          {model.metadata.creators.length > 0 ? (
            <span className="book-toc__author truncate">
              {model.metadata.creators.join(', ')}
            </span>
          ) : null}
        </div>
        <ol className="book-toc__list">
          {model.chapters.map((c) => (
            <li key={c.part}>
              <button
                className="book-toc__item"
                aria-current={c.index === current ? 'true' : undefined}
                onClick={() => onSelect(c.index)}
              >
                <span className="truncate">{c.title}</span>
                <span className="book-toc__words tnum">{c.wordCount}</span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <div className="canvas book-canvas" ref={column}>
        <article
          className="book-page"
          style={{ ['--doc-zoom' as string]: String(zoom) }}
          lang={model.metadata.language === 'und' ? undefined : model.metadata.language}
          aria-label={chapter.title}
        >
          <h1 className="book-page__title">{chapter.title}</h1>
          {chapter.blocks.map((block, i) => (
            <Block key={i} block={block} search={search} imageUrls={imageUrls} />
          ))}

          <ChapterNav
            model={model}
            current={current}
            onSelect={onSelect}
          />
        </article>
      </div>
    </div>
  );
}

function Block(
  { block, search, imageUrls }: {
    block: EpubBlock;
    search: string;
    imageUrls: ReadonlyMap<string, string>;
  },
): React.JSX.Element | null {
  if (block.kind === 'image') {
    const url = block.part ? imageUrls.get(block.part) : undefined;
    if (!url) {
      return (
        <figure className="book-figure book-figure--missing">
          <Icon name="file-image" size={18} />
          <figcaption>{block.alt || '표시할 수 없는 그림'}</figcaption>
        </figure>
      );
    }
    return (
      <figure className="book-figure">
        <img src={url} alt={block.alt} />
        {block.alt ? <figcaption>{block.alt}</figcaption> : null}
      </figure>
    );
  }

  const content = search ? highlight(block.text, search) : block.text;

  if (block.kind === 'heading') {
    // The chapter's own title is already the h1, so the book's headings start
    // one level down — a heading hierarchy that skips is what breaks
    // navigation by heading for a screen reader (§26).
    const Tag = `h${Math.min(block.level + 1, 6)}` as 'h2';
    return <Tag className="book-h">{content}</Tag>;
  }
  if (block.kind === 'quote') return <blockquote className="book-quote">{content}</blockquote>;
  if (block.kind === 'list-item') return <p className="book-li">{content}</p>;
  return <p className="book-p">{content}</p>;
}

function ChapterNav(
  { model, current, onSelect }: {
    model: EpubModel; current: number; onSelect: (index: number) => void;
  },
): React.JSX.Element {
  const previous = model.chapters[current - 1];
  const next = model.chapters[current + 1];
  return (
    <nav className="book-nav" aria-label="장 이동">
      {previous ? (
        <button className="btn btn--outline" onClick={() => onSelect(previous.index)}>
          <Icon name="chevron-left" size={14} />
          <span className="truncate">{previous.title}</span>
        </button>
      ) : <span />}
      {next ? (
        <button className="btn btn--outline" onClick={() => onSelect(next.index)}>
          <span className="truncate">{next.title}</span>
          <Icon name="chevron-right" size={14} />
        </button>
      ) : <span />}
    </nav>
  );
}

/** Chapter containing the first match, for jumping search to it. */
export function chapterWithMatch(model: EpubModel, needle: string): EpubChapter | null {
  if (!needle.trim()) return null;
  const target = needle.toLowerCase();
  return model.chapters.find((c) =>
    c.blocks.some((b) => b.text.toLowerCase().includes(target))) ?? null;
}

function highlight(text: string, needle: string): React.ReactNode {
  if (!needle) return text;
  const lower = text.toLowerCase();
  const target = needle.toLowerCase();
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (;;) {
    const found = lower.indexOf(target, cursor);
    if (found < 0) break;
    if (found > cursor) out.push(text.slice(cursor, found));
    out.push(<mark key={found}>{text.slice(found, found + needle.length)}</mark>);
    cursor = found + needle.length;
  }
  if (out.length === 0) return text;
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
